import React from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { COUNTRIES } from '@/lib/trustHub/v1/countries';
import { ProviderId } from '@/lib/trustHub/v1/types';
import { Play, Pause, Square, Lock } from 'lucide-react';

interface InputPanelProps {
  queries: string;
  onQueriesChange: (queries: string) => void;
  selectedProviders: Set<ProviderId>;
  onProviderToggle: (provider: ProviderId) => void;
  country: string;
  onCountryChange: (country: string) => void;
  useCache: boolean;
  onUseCacheChange: (useCache: boolean) => void;
  saveSnapshot: boolean;
  onSaveSnapshotChange: (save: boolean) => void;
  isProcessing: boolean;
  isPaused: boolean;
  unlockPassword: string;
  onUnlockPasswordChange: (password: string) => void;
  isUnlocked: boolean;
  onRun: () => void;
  onPause: () => void;
  onStop: () => void;
}

export const InputPanel: React.FC<InputPanelProps> = ({
  queries,
  onQueriesChange,
  selectedProviders,
  onProviderToggle,
  country,
  onCountryChange,
  useCache,
  onUseCacheChange,
  saveSnapshot,
  onSaveSnapshotChange,
  isProcessing,
  isPaused,
  unlockPassword,
  onUnlockPasswordChange,
  isUnlocked,
  onRun,
  onPause,
  onStop,
}) => {
  const queryLines = queries.split('\n').filter(q => q.trim());
  const queryCount = queryLines.length;
  const baseLimit = 100;
  const extendedLimit = 1000;
  const maxQueries = isUnlocked ? extendedLimit : baseLimit;
  const needsUnlock = queryCount > baseLimit && !isUnlocked;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="queries" className="text-lg font-semibold">
            Queries
          </Label>
          <span className={`text-sm font-medium ${queryCount > maxQueries ? 'text-destructive' : 'text-muted-foreground'}`}>
            {queryCount} of {maxQueries} queries {!isUnlocked && `(unlock for up to ${extendedLimit})`}
          </span>
        </div>
        <Textarea
          id="queries"
          placeholder={`Enter queries (one per line, up to ${maxQueries})`}
          value={queries}
          onChange={(e) => onQueriesChange(e.target.value)}
          className="min-h-[200px] font-mono text-sm"
          disabled={isProcessing}
        />
      </div>

      {/* Password unlock section */}
      {queryCount > baseLimit && !isUnlocked && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Enter password to unlock up to {extendedLimit} queries:
          </span>
          <Input
            type="password"
            value={unlockPassword}
            onChange={(e) => onUnlockPasswordChange(e.target.value)}
            placeholder="Password"
            className="w-40"
            disabled={isProcessing}
          />
        </div>
      )}
      
      {isUnlocked && (
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <Lock className="h-4 w-4" />
          Extended mode unlocked - up to {extendedLimit} queries
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>AI Providers</Label>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="openai"
                checked={selectedProviders.has('openai')}
                onCheckedChange={() => onProviderToggle('openai')}
                disabled={isProcessing}
              />
              <Label htmlFor="openai" className="cursor-pointer">OpenAI (GPT-4.1)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="claude"
                checked={selectedProviders.has('claude')}
                onCheckedChange={() => onProviderToggle('claude')}
                disabled={isProcessing}
              />
              <Label htmlFor="claude" className="cursor-pointer">Claude (Sonnet 4.5)</Label>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="country">Target Country</Label>
          <Select value={country} onValueChange={onCountryChange} disabled={isProcessing}>
            <SelectTrigger id="country">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.flag} {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="useCache"
            checked={useCache}
            onCheckedChange={(checked) => onUseCacheChange(checked === true)}
            disabled={isProcessing}
          />
          <Label htmlFor="useCache" className="text-sm cursor-pointer">
            Use cache
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="saveSnapshot"
            checked={saveSnapshot}
            onCheckedChange={(checked) => onSaveSnapshotChange(checked === true)}
            disabled={isProcessing}
          />
          <Label htmlFor="saveSnapshot" className="text-sm cursor-pointer">
            Save snapshot
          </Label>
        </div>
      </div>

      <div className="flex gap-3">
        {!isProcessing ? (
          <Button
            onClick={onRun}
            disabled={queryCount === 0 || queryCount > maxQueries || selectedProviders.size === 0}
            className="flex-1"
            size="lg"
          >
            <Play className="h-4 w-4 mr-2" />
            Run Analysis
          </Button>
        ) : (
          <>
            <Button
              onClick={onPause}
              variant="outline"
              className="flex-1"
              size="lg"
            >
              <Pause className="h-4 w-4 mr-2" />
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
            <Button
              onClick={onStop}
              variant="destructive"
              className="flex-1"
              size="lg"
            >
              <Square className="h-4 w-4 mr-2" />
              Stop
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
